/**
 * Hosted app (Azure) with the timesheet on SAP CATS.
 *
 * The BSP build inside S/4 is on CATS from the first line of code: same-origin
 * OData, identity from the SAP session (identityResolver.ts). The hosted app
 * serves many organizations, so it decides at runtime: the server says whether
 * the signed-in organization is connected to SAP and who the person is in SAP
 * (GET /api/timesheet-sap/identity, migration 00593). When it is:
 *
 *   1. `enableHostedSapTimesheet()` flips IS_LIVE_SAP, so every timesheet
 *      service takes its CATS branch (the same code the BSP build runs);
 *   2. a fetch interceptor sends the services' same-origin `/sap/opu/odata4/...`
 *      calls through /api/s4 with the organization header. The server stamps
 *      ActingUser / ActingPernr on every action there, so SAP records whose
 *      time it is and who entered it. CSRF is the proxy's job on this host,
 *      so the client's CSRF fetch is answered locally with no token;
 *   3. the current employee is pinned to the personnel number the server
 *      resolved (the impersonated one while impersonating);
 *   4. reference data is re-primed from SAP.
 *
 * Patching fetch once mirrors lib/apiBearer.ts and the Entra interceptor: ~10
 * timesheet services build `/sap/opu/...` URLs, and editing each would leave
 * the next one added pointing at the SPA fallback.
 */
import { useEffect } from 'react'
import { create } from 'zustand'
import { useAppStore } from '@/store/appStore'
import { IS_S4_BSP_BUILD, IS_HOSTED_SAP, enableHostedSapTimesheet, disableHostedSapTimesheet } from '@/config/sapBackend'
import { useAuthStore } from '@/store/authStore'
import { isEntraAuthEnabled } from '@/lib/entraAuth'
import { useTimesheetStore } from '../stores/timesheetStore'

export interface HostedSapIdentity {
  orgId: string
  /** This person's timesheet is on SAP (organization on SAP and person mapped). */
  enabled: boolean
  /** The organization is on SAP, whether or not this person is mapped yet. */
  orgEnabled?: boolean
  userId: string | null
  email: string | null
  name: string | null
  ownPernr: string | null
  effectivePernr: string | null
  isImpersonating: boolean
  impersonation: {
    sessionId: string
    targetUserId: string
    targetName: string | null
    targetPernr: string
    reason: string
    startedAt: string
  } | null
  canImpersonate: boolean
  isAdmin: boolean
  roles: string[]
}

type Status = 'unknown' | 'loading' | 'off' | 'on' | 'error'

interface HostedSapState {
  status: Status
  orgId: string | null
  identity: HostedSapIdentity | null
  error: string | null
}

export const useHostedSapStore = create<HostedSapState>(() => ({
  status: IS_S4_BSP_BUILD ? 'off' : 'unknown',
  orgId: null,
  identity: null,
  error: null,
}))

const SAP_ODATA_PREFIX = '/sap/opu/odata4/'
const PROXY_PREFIX = '/api/s4/odata/v4'

let interceptorOrg: string | null = null

function installSapFetchInterceptor(orgId: string): void {
  if (typeof window === 'undefined') return
  if (interceptorOrg !== null) {
    interceptorOrg = orgId
    return
  }
  interceptorOrg = orgId
  const inner = window.fetch.bind(window)
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const rawUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
      const url = new URL(rawUrl, window.location.origin)
      if (url.origin === window.location.origin && url.pathname.startsWith(SAP_ODATA_PREFIX)) {
        const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()
        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
        // CSRF is fetched and sent by the proxy on this host.
        if (method === 'HEAD' && headers.get('X-CSRF-Token')?.toLowerCase() === 'fetch') {
          return new Response(null, { status: 200 })
        }
        headers.delete('X-CSRF-Token')
        headers.set('x-ppm-org', interceptorOrg ?? orgId)
        const target = `${PROXY_PREFIX}${url.pathname}${url.search}`
        const body = init?.body ?? (input instanceof Request && method !== 'GET' && method !== 'HEAD' ? await input.clone().text() : undefined)
        return inner(target, { ...init, method, headers, body, credentials: 'same-origin' })
      }
    } catch {
      /* fall through to the unmodified request */
    }
    return inner(input, init)
  }
}

async function fetchIdentity(orgId: string): Promise<HostedSapIdentity> {
  const res = await fetch('/api/timesheet-sap/identity', { headers: { Accept: 'application/json', 'x-ppm-org': orgId } })
  const body = (await res.json().catch(() => ({}))) as Partial<HostedSapIdentity> & { error?: string }
  if (!res.ok) throw new Error(body.error || `Timesheet identity: HTTP ${res.status}`)
  return body as HostedSapIdentity
}

let inflight: Promise<void> | null = null
let inflightOrg: string | null = null

/**
 * Decide whether this session's timesheet is on SAP CATS for this
 * organization, and switch over (or back) when it is (or is not). Safe to call
 * from several components; calls for the same organization share one request.
 * Switching happens in place: the switch is a live binding the services read at
 * call time, so a reload is never needed (one would loop, because the roster
 * binding can change the organization right after start-up).
 */
export function ensureHostedSapTimesheet(orgId: string | null | undefined): Promise<void> {
  if (IS_S4_BSP_BUILD) return Promise.resolve()
  const org = (orgId ?? '').trim()
  if (!org) {
    if (useHostedSapStore.getState().status === 'unknown') useHostedSapStore.setState({ status: 'off' })
    return Promise.resolve()
  }
  const st = useHostedSapStore.getState()
  if (st.orgId === org && (st.status === 'on' || st.status === 'off')) return Promise.resolve()
  if (inflight && inflightOrg === org) return inflight

  inflightOrg = org
  useHostedSapStore.setState({ status: 'loading' })
  inflight = (async () => {
    try {
      const identity = await fetchIdentity(org)
      const ds = await import('../services/timesheetDataService')
      if (!identity.enabled) {
        if (IS_HOSTED_SAP) {
          // Moved from an SAP organization to one that is not: back to the
          // app database, primed afresh.
          disableHostedSapTimesheet()
          useTimesheetStore.getState().pinSapEmployee(null)
          ds.resetReferenceDataForSap()
          await ds.bootstrapReferenceData(true)
          useAppStore.getState().bumpReferenceDataVersion()
        }
        useHostedSapStore.setState({ status: 'off', orgId: org, identity, error: null })
        return
      }
      enableHostedSapTimesheet()
      installSapFetchInterceptor(org)
      applyIdentity(identity)
      ds.resetReferenceDataForSap()
      await ds.bootstrapReferenceData(true)
      useHostedSapStore.setState({ status: 'on', orgId: org, identity, error: null })
      useAppStore.getState().bumpReferenceDataVersion()
    } catch (err) {
      // Not cached as a decision: the next call (a new organization, or the
      // session finishing sign-in) asks again.
      console.warn('[timesheet] SAP CATS identity check failed; the timesheet stays on the app database', err)
      useHostedSapStore.setState({ status: 'error', orgId: null, error: err instanceof Error ? err.message : String(err) })
    } finally {
      inflight = null
      inflightOrg = null
    }
  })()
  return inflight
}

function applyIdentity(identity: HostedSapIdentity): void {
  useTimesheetStore.getState().pinSapEmployee(identity.effectivePernr)
}

/** Re-read identity after impersonation starts or stops. */
export async function refreshHostedSapIdentity(): Promise<HostedSapIdentity | null> {
  const org = useHostedSapStore.getState().orgId
  if (!org || !IS_HOSTED_SAP) return null
  const identity = await fetchIdentity(org)
  applyIdentity(identity)
  useHostedSapStore.setState({ identity })
  return identity
}

async function postJson(url: string, method: 'POST' | 'DELETE', body?: unknown): Promise<unknown> {
  const org = useHostedSapStore.getState().orgId ?? ''
  const res = await fetch(url, {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'x-ppm-org': org },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as { error?: string }
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
  return json
}

/**
 * Start impersonating someone (server-side, audit-logged). Name them by
 * personnel number: a shop-floor worker may exist only in SAP, with no login.
 */
export async function startHostedImpersonation(targetPernr: string, reason: string): Promise<HostedSapIdentity | null> {
  await postJson('/api/timesheet-sap/impersonate', 'POST', { targetPernr, reason })
  return refreshHostedSapIdentity()
}

/** Stop impersonating. */
export async function stopHostedImpersonation(): Promise<HostedSapIdentity | null> {
  await postJson('/api/timesheet-sap/impersonate', 'DELETE')
  return refreshHostedSapIdentity()
}

/** Admin: switch the current organization onto (or off) SAP CATS. */
export async function setOrgOnSapCats(enabled: boolean, sapDestination?: string | null, note?: string): Promise<void> {
  await postJson('/api/timesheet-sap/admin', 'POST', { op: 'setOrg', enabled, sapDestination: sapDestination ?? null, note })
}

/** Admin: map a person to a personnel number in the current organization. */
export async function mapUserToPernr(userId: string, pernr: string, canImpersonate = false): Promise<void> {
  await postJson('/api/timesheet-sap/admin', 'POST', { op: 'mapUser', userId, pernr, canImpersonate })
}

/** Admin: deactivate a person's mapping. */
export async function unmapUser(userId: string): Promise<void> {
  await postJson('/api/timesheet-sap/admin', 'POST', { op: 'unmapUser', userId })
}

/**
 * Resolve the SAP CATS switch for the signed-in organization and report where
 * it stands. Components that read IS_LIVE_SAP subscribe through this so they
 * re-render once the switch lands. `ready` is false only while it is being
 * decided for the first time.
 */
export function useHostedSapTimesheet(): { status: Status; ready: boolean; identity: HostedSapIdentity | null } {
  // Same organization every timesheet service uses (useTimeEntries, charge
  // objects, approvals): the person's org, else the demo org.
  const orgId = useAppStore((s) => s.currentUser?.organizationId ?? 'org_demo')
  const status = useHostedSapStore((s) => s.status)
  const identity = useHostedSapStore((s) => s.identity)
  // Ask only once the session is signed in: before that the identity call has
  // no token and answers 401. Re-asked when the sign-in or the org changes.
  const signedIn = useAuthStore((s) => !!s.user) || !isEntraAuthEnabled()
  useEffect(() => {
    if (signedIn) void ensureHostedSapTimesheet(orgId)
  }, [orgId, signedIn])
  return { status, ready: status !== 'unknown' && status !== 'loading', identity }
}

/** Test seam. */
export function __resetHostedSapForTests(): void {
  inflight = null
  inflightOrg = null
  interceptorOrg = null
  useHostedSapStore.setState({ status: 'unknown', orgId: null, identity: null, error: null })
}
